import { toast } from "react-toastify";
import { t } from "i18next";
import {
    formatSets,
    getGroupRoundNumber,
    getOrdinalSuffix,
    getRoundTitle,
    groupCharCode,
    MatchStatus,
    formatLastName,
    COUNTRY_OPTIONS,
    getGradeName,
    genders
} from "../../../constants";
import { getOptionName } from "../../../utils/getOptionName";
import { PRINT_THEME as T } from "./printTheme";

export const copyToClipboard = async (tsvText, htmlText) => {
    try {
        if (navigator.clipboard && window.ClipboardItem && htmlText) {
            const textBlob = new Blob([tsvText || ""], { type: "text/plain" });
            const htmlBlob = new Blob([htmlText || ""], { type: "text/html" });
            await navigator.clipboard.write([
                new ClipboardItem({
                    "text/plain": textBlob,
                    "text/html": htmlBlob
                })
            ]);
            toast.success(t("Table copied! Paste into Google Sheets/Docs or Excel (Ctrl+V)"));
            return true;
        } else if (navigator.clipboard) {
            await navigator.clipboard.writeText(tsvText);
            toast.success(t("Data copied to clipboard!"));
            return true;
        } else {
            const textarea = document.createElement("textarea");
            textarea.value = tsvText;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            toast.success(t("Data copied to clipboard!"));
            return true;
        }
    } catch (e) {
        console.error("Clipboard copy failed", e);
        try {
            if (navigator.clipboard) {
                await navigator.clipboard.writeText(tsvText);
                toast.success(t("Data copied to clipboard!"));
                return true;
            }
        } catch (err2) {
            console.error("Fallback clipboard failed", err2);
        }
        toast.error(t("Failed to copy to clipboard"));
        return false;
    }
};

// Dynamic import: xlsx alone is ~280KB (+ ~45KB for its zip dep, pako) of
// this bundle's JS for a feature most visits never touch - loaded on demand,
// right when the user actually exports to Excel, instead of shipping it to
// every page load.
export const exportWorkbook = async (sheetsMap, defaultFileName) => {
    try {
        const XLSX = await import("xlsx");
        const wb = XLSX.utils.book_new();
        const entries = Object.entries(sheetsMap);
        if (!entries.length) {
            const ws = XLSX.utils.aoa_to_sheet([[t("No data available")]]);
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
        } else {
            entries.forEach(([sheetName, aoa]) => {
                const cleanSheetName = (sheetName || "Sheet1").replace(/[:\\/?*\[\]]/g, "_").slice(0, 31);
                const ws = XLSX.utils.aoa_to_sheet(aoa && aoa.length ? aoa : [[t("No data available")]]);

                // Auto-calculate column widths
                const colWidths = [];
                (aoa || []).forEach(row => {
                    (row || []).forEach((cell, cIdx) => {
                        const str = cell != null ? String(cell) : "";
                        colWidths[cIdx] = Math.max(colWidths[cIdx] || 10, Math.min(str.length + 3, 45));
                    });
                });
                if (colWidths.length) {
                    ws["!cols"] = colWidths.map(w => ({ wch: w }));
                }

                XLSX.utils.book_append_sheet(wb, ws, cleanSheetName);
            });
        }

        XLSX.writeFile(wb, `${defaultFileName}.xlsx`);
        toast.success(t("Excel file saved!"));
    } catch (e) {
        console.error("Excel export error", e);
        toast.error(t("Excel export failed"));
    }
};

export const formatPlayerName = (p) => {
    if (!p) return "";
    if (typeof p === "string") return p;
    // `.player` is a nested profile object on some shapes (e.g. rating list
    // entries) but a plain FK id *string* on team roster entries
    // (GET .../teams/ returns { player: "<uuid>", first_name, last_name, ... }
    // flattened directly onto the roster item) - unwrapping unconditionally
    // picked that uuid string as `obj` there, so every `.last_name`/`.first_name`
    // lookup silently resolved to undefined and the name rendered blank.
    const obj = (p.player && typeof p.player === "object") ? p.player : p;
    const last = formatLastName(obj.last_name || "");
    const first = obj.first_name || "";
    if (last && first) return `${first ? first[0] + '. ' : ''}${last}`;
    return last || first || obj.name || "";
};

export const formatDoublesPair = (p1, p2) => {
    const name1 = formatPlayerName(p1);
    const name2 = formatPlayerName(p2);
    if (name1 && name2) return `${name1} / ${name2}`;
    return name1 || name2 || "";
};

// "LASTNAME Firstname" - the entry-list convention real ATP/WTA/ITF acceptance
// lists use, distinct from formatPlayerName's compact "F. Lastname" (which
// stays as-is for schedule/bracket/group cells that need the shorter form).
export const formatEntryListName = (p) => {
    if (!p) return "";
    if (typeof p === "string") return p;
    const obj = (p.player && typeof p.player === "object") ? p.player : p;
    const last = (obj.last_name || "").toUpperCase();
    const first = obj.first_name || "";
    if (last && first) return `${last} ${first}`;
    return last || first || obj.name || "";
};

export const formatEntryListDoublesPair = (p1, p2) => {
    const name1 = formatEntryListName(p1);
    const name2 = formatEntryListName(p2);
    if (name1 && name2) return `${name1} / ${name2}`;
    return name1 || name2 || "";
};

// Team roster (grade 3) - `players` here is the same team.players array
// renderSlotCardTeam (TournamentPlayers.js) reads, ordered by their slot
// (`.sorting`) rather than array/API order, which isn't guaranteed to match.
export const formatTeamMembers = (players) => {
    if (!Array.isArray(players) || !players.length) return "";
    return players
        .slice()
        .sort((a, b) => (a?.sorting ?? 0) - (b?.sorting ?? 0))
        .map(p => formatEntryListName(p))
        .filter(Boolean)
        .join(", ");
};

export const getMatchParticipantNames = (match, grade = 1) => {
    if (!match) return { side1: "", side2: "" };
    if (grade === 3) {
        const side1 = match.team_1_name || (match.team_1_number ? `${t("Team")} ${match.team_1_number}` : t("Team 1"));
        const side2 = match.team_2_name || (match.team_2_number ? `${t("Team")} ${match.team_2_number}` : t("Team 2"));
        return { side1, side2 };
    }
    if (grade === 2) {
        const side1 = formatDoublesPair(match.player_1_name, match.player_3_name) || t("Pair 1");
        const side2 = formatDoublesPair(match.player_2_name, match.player_4_name) || t("Pair 2");
        return { side1, side2 };
    }
    const side1 = formatPlayerName(match.player_1_name) || t("Player 1");
    const side2 = formatPlayerName(match.player_2_name) || t("Player 2");
    return { side1, side2 };
};

export const getMatchScoreText = (match) => {
    if (!match) return "";
    const setsScore = formatSets(match);
    if (setsScore && setsScore.trim()) return setsScore;
    if (match.status && match.status !== 1) {
        return t(MatchStatus[match.status] || "");
    }
    return "";
};

export const formatTournamentLocation = (tournament) => {
    const locName = (typeof tournament?.location_name === 'string' && tournament.location_name.trim())
        ? tournament.location_name.trim()
        : (typeof tournament?.location === 'string' && !/^\d+$/.test(tournament.location.trim()) ? tournament.location.trim() : "");

    const city = (typeof tournament?.city === 'string') ? tournament.city.trim() : "";

    let country = "";
    if (tournament?.country) {
        const countryName = getOptionName(COUNTRY_OPTIONS, tournament.country);
        if (countryName && !/^\d+$/.test(String(countryName).trim())) {
            country = t(countryName);
        }
    }

    const parts = [locName, city, country].filter(Boolean);
    return parts.join(", ");
};

export const getTournamentExportHeaderInfo = (tournament, stageName) => {
    const club = (typeof tournament?.club_name === 'string' && tournament.club_name.trim())
        || (typeof tournament?.club === 'string' && !/^\d+$/.test(tournament.club.trim()) ? tournament.club.trim() : "");

    // Same denormalized field TournamentsList.js/ClubProfileModal.js already
    // read straight into an <img src> - already an absolute URL, no /media/
    // prefixing needed (unlike TournamentHeader.js's separate club.image).
    const logo = tournament?.club_logo || "";

    const location = formatTournamentLocation(tournament);

    const genderObj = tournament?.gender ? genders.find(g => g.id === tournament.gender) : null;
    const genderName = genderObj ? (tournament.gender === 3 && tournament.grade === 1 ? t("Admixed") : t(genderObj.name)) : null;
    const formatParts = [
        getGradeName(tournament?.grade),
        genderName,
        (typeof tournament?.format === 'string' && !/^\d+$/.test(tournament.format.trim())) ? tournament.format.trim() : null
    ].filter(Boolean);
    const formatStr = formatParts.join(" · ");

    const dateRange = tournament?.start_date && tournament?.end_date
        ? tournament.start_date === tournament.end_date
            ? new Date(tournament.start_date).toLocaleDateString("uk-UA")
            : `${new Date(tournament.start_date).toLocaleDateString("uk-UA")} – ${new Date(tournament.end_date).toLocaleDateString("uk-UA")}`
        : "";

    const name = tournament?.name || t("Tournament");

    const cleanStage = (stageName && stageName !== t("Participants") && stageName !== t("Results") && !/^\d+$/.test(String(stageName).trim()))
        ? stageName
        : "";

    const metaRow1 = [
        club ? `${t("Club")}: ${club}` : "",
        location ? `${t("Location")}: ${location}` : ""
    ].filter(Boolean).join("  |  ");

    const metaRow2 = [
        formatStr ? `${t("Format")}: ${formatStr}` : "",
        cleanStage ? `${t("Stage")}: ${cleanStage}` : "",
        dateRange ? `${t("Date")}: ${dateRange}` : ""
    ].filter(Boolean).join("  |  ");

    const aoaHeader = [
        [name],
        ...(metaRow1 ? [[metaRow1]] : []),
        ...(metaRow2 ? [[metaRow2]] : []),
        []
    ];

    const htmlHeader = `
        <div style="display: flex; align-items: center; gap: 14px; margin-bottom: 16px; border-bottom: 2px solid ${T.border}; padding-bottom: 12px; font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
            ${logo ? `<img src="${logo}" alt="" style="width: 48px; height: 48px; border-radius: 50%; object-fit: cover; flex: none;" />` : ""}
            <div>
                <div style="font-size: 20px; font-weight: bold; color: ${T.ink}; margin-bottom: 6px;">${name}</div>
                <div style="font-size: 13px; color: ${T.muted}; line-height: 1.6;">
                    ${club ? `<span style="margin-right: 14px;"><strong style="color: ${T.ink};">${t("Club")}:</strong> ${club}</span>` : ""}
                    ${location ? `<span style="margin-right: 14px;"><strong style="color: ${T.ink};">${t("Location")}:</strong> ${location}</span>` : ""}
                    ${formatStr ? `<span style="margin-right: 14px;"><strong style="color: ${T.ink};">${t("Format")}:</strong> ${formatStr}</span>` : ""}
                    ${cleanStage ? `<span style="margin-right: 14px;"><strong style="color: ${T.ink};">${t("Stage")}:</strong> <span style="background: ${T.border}; color: ${T.ink}; padding: 2px 6px; border-radius: 4px; font-weight: bold;">${cleanStage}</span></span>` : ""}
                    ${dateRange ? `<span><strong style="color: ${T.ink};">${t("Date")}:</strong> ${dateRange}</span>` : ""}
                </div>
            </div>
        </div>
    `;

    return { name, club, location, formatStr, dateRange, stage: cleanStage, logo, aoaHeader, htmlHeader };
};

// ==================== SCHEDULE BUILDERS ====================\n

export const buildScheduleDataset = (tournament, stageName, scheduleData, grade = 1, i18n) => {
    const { courts = [], rounds = [] } = scheduleData || {};
    const { aoaHeader, htmlHeader } = getTournamentExportHeaderInfo(tournament, stageName);

    if (!courts.length && !rounds.length) {
        return {
            aoa: [...aoaHeader, [t("No data available")]],
            tsv: `${stageName || t("Schedule")}\n${t("No data available")}`,
            html: `<div>${htmlHeader}<p style="color: ${T.muted}; font-style: italic;">${t("No data available")}</p></div>`
        };
    }

    const tableHeaders = [
        `${t("Round")} / ${t("Time")}`,
        ...courts.map(c => `${t("Court")} ${c.number || c.name || ""}`)
    ];

    const rows = {};
    rounds.forEach(round => {
        if (!rows[round.order]) rows[round.order] = {};
        rows[round.order][round.court] = round;
    });

    const aoaRows = [];
    const tsvRows = [tableHeaders.join("\t")];
    let htmlTable = `<div>\n${htmlHeader}\n<table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; border: 1px solid ${T.border};">\n`;
    htmlTable += `<thead><tr>`;
    tableHeaders.forEach(th => {
        htmlTable += `<th style="background: ${T.superWhite}; color: ${T.ink}; font-weight: 700; border: 1px solid ${T.border}; padding: 8px;">${th}</th>`;
    });
    htmlTable += `</tr></thead><tbody>\n`;

    Object.keys(rows).forEach((order, index) => {
        const rowCells = Object.values(rows[order]);
        const firstRoundInRow = rowCells[0];
        const timeStr = firstRoundInRow?.start_time || "";
        const rowHeader = `№ ${index + 1}${timeStr ? ` (${timeStr})` : ""}`;

        const rowVals = [rowHeader];
        let htmlRow = `<tr style="background: ${index % 2 === 1 ? T.superWhite : T.white};"><td style="font-weight: bold; background: ${T.superWhite}; color: ${T.ink}; text-align: center; border: 1px solid ${T.border}; padding: 8px;">${rowHeader}</td>`;

        courts.forEach(court => {
            const round = rows[order]?.[court.id];
            const match = round?.match;

            if (!match) {
                rowVals.push("—");
                htmlRow += `<td style="color: ${T.muted}; text-align: center; border: 1px solid ${T.border}; padding: 8px;">—</td>`;
                return;
            }

            const { side1, side2 } = getMatchParticipantNames(match, grade);
            const scoreText = getMatchScoreText(match);
            const groupRound = getGroupRoundNumber(match.total, match.match_order);
            const stageDesc = match.group_number
                ? `${t("Group")} ${groupCharCode(match.group_number)}${groupRound ? ` (${t("R.")} ${groupRound})` : ""}`
                : `${match.draw_number ? `${match.draw_number}${getOrdinalSuffix(match.draw_number)} ${t("Place")} · ` : ""}${getRoundTitle(match.round_number, match.total, t, i18n, match.match_order, false)}`;

            const cellText = `${stageDesc} | ${side1} vs ${side2}${scoreText ? ` [${scoreText}]` : ""}`;
            rowVals.push(cellText);

            htmlRow += `<td style="border: 1px solid ${T.border}; padding: 8px; vertical-align: top;">
                <div style="font-size: 11px; color: ${T.muted}; margin-bottom: 2px;">${stageDesc}</div>
                <div style="font-weight: ${match.winner_side === 1 ? 'bold' : '500'}; color: ${match.winner_side === 1 ? T.darkerGreen : T.ink};">${side1}</div>
                <div style="font-size: 10px; color: ${T.muted};">vs</div>
                <div style="font-weight: ${match.winner_side === 2 ? 'bold' : '500'}; color: ${match.winner_side === 2 ? T.darkerGreen : T.ink};">${side2}</div>
                ${scoreText ? `<div style="font-size: 11px; color: ${T.ink}; font-weight: bold; margin-top: 3px; text-align: right;">${scoreText}</div>` : ""}
            </td>`;
        });

        htmlRow += `</tr>\n`;
        aoaRows.push(rowVals);
        tsvRows.push(rowVals.join("\t"));
        htmlTable += htmlRow;
    });

    htmlTable += `</tbody>\n</table></div>`;

    return {
        aoa: [...aoaHeader, tableHeaders, ...aoaRows],
        tsv: tsvRows.join("\n"),
        html: htmlTable
    };
};

// ==================== GROUPS BUILDERS ====================\n

export const buildGroupsDataset = (tournament, stageName, groupsData, grade = 1) => {
    const groups = Array.isArray(groupsData) ? groupsData : (groupsData?.groups || []);
    const { aoaHeader, htmlHeader } = getTournamentExportHeaderInfo(tournament, stageName);

    if (!groups.length) {
        return {
            sheets: { [stageName || "Sheet1"]: [...aoaHeader, [t("No data available")]] },
            tsv: `${stageName || t("Groups")}\n${t("No data available")}`,
            html: `<div>${htmlHeader}<p style="color: ${T.muted}; font-style: italic;">${t("No data available")}</p></div>`
        };
    }

    const getMatch = (group, row, col) => {
        if (row === col) return null;
        const n = group.number_players || (group.players ? group.players.length : 4);
        let index = 0;
        for (let r = 0; r < n; r++) {
            for (let c = r + 1; c < n; c++) {
                if ((r === row && c === col) || (r === col && c === row)) {
                    return group.matches?.[index];
                }
                index++;
            }
        }
        return null;
    };

    const sheets = {};
    const tsvAll = [];
    let htmlAll = `<div>\n${htmlHeader}\n`;

    groups.forEach((group) => {
        const groupLabel = `${t("Group")} ${groupCharCode(group.number)} (${group.number})`;
        const playersCount = group.number_players || (group.players ? group.players.length : 4);
        const playerNumbers = Array.from({ length: playersCount }, (_, i) => i + 1);

        // Standings calculation
        const dynamicStats = Array.from({ length: playersCount }, () => ({ games: 0, points: 0, hasStats: false }));
        let mIndex = 0;
        for (let r = 0; r < playersCount; r++) {
            for (let c = r + 1; c < playersCount; c++) {
                const match = group.matches?.[mIndex++];
                if (match && match.sets && match.sets.length) {
                    let g1 = 0, g2 = 0;
                    match.sets.forEach(s => {
                        g1 += Number(s.game_score_1) || 0;
                        g2 += Number(s.game_score_2) || 0;
                    });
                    dynamicStats[r].games += (g1 - g2);
                    dynamicStats[c].games += (g2 - g1);
                    dynamicStats[r].points += (match.winner_side === 1 ? 1 : (g1 > g2 ? 1 : 0));
                    dynamicStats[c].points += (match.winner_side === 2 ? 1 : (g2 > g1 ? 1 : 0));
                    dynamicStats[r].hasStats = true;
                    dynamicStats[c].hasStats = true;
                }
            }
        }

        const tableHeaders = ["№", t("Participants"), ...playerNumbers.map(String), t("+/-"), t("PTS"), t("Pl")];
        const aoaGroup = [
            ...aoaHeader,
            [groupLabel],
            tableHeaders
        ];

        tsvAll.push(`\n=== ${groupLabel} ===`);
        tsvAll.push(tableHeaders.join("\t"));

        let htmlTable = `<div style="margin-bottom: 24px; border: 1px solid ${T.border}; border-radius: 6px; overflow: hidden;">\n`;
        htmlTable += `<div style="background: ${T.superWhite}; color: ${T.ink}; border-bottom: 1px solid ${T.border}; padding: 8px 12px; font-weight: bold; font-size: 14px;">${groupLabel}</div>\n`;
        htmlTable += `<table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; border: 1px solid ${T.border};">\n`;
        htmlTable += `<thead><tr>`;
        tableHeaders.forEach(th => {
            htmlTable += `<th style="background: ${T.superWhite}; color: ${T.ink}; font-weight: 700; border: 1px solid ${T.border}; padding: 7px;">${th}</th>`;
        });
        htmlTable += `</tr></thead><tbody>\n`;

        Array.from({ length: playersCount }, (_, rowIndex) => {
            const p = group.players?.[rowIndex] || group.participants?.[rowIndex];
            let name = `—`;
            if (p) {
                if (grade === 1) {
                    name = formatPlayerName(p);
                } else if (grade === 2) {
                    const pair = p.players || p.team?.players || (Array.isArray(p) ? p : []);
                    name = formatDoublesPair(pair[0], pair[1]);
                } else {
                    name = p.name || p.team?.name || `${t("Team")} ${rowIndex + 1}`;
                }
            }

            const rowVals = [rowIndex + 1, name];
            let htmlRow = `<tr style="background: ${rowIndex % 2 === 1 ? T.superWhite : T.white};"><td style="text-align: center; font-weight: bold; background: ${T.superWhite}; color: ${T.ink}; border: 1px solid ${T.border};">${rowIndex + 1}</td><td style="font-weight: 500; border: 1px solid ${T.border}; padding: 6px 10px;">${name}</td>`;

            playerNumbers.forEach((_, colIndex) => {
                if (rowIndex === colIndex) {
                    rowVals.push("X");
                    htmlRow += `<td style="text-align: center; background: ${T.border}; color: ${T.muted}; border: 1px solid ${T.border};">●</td>`;
                    return;
                }
                const match = getMatch(group, rowIndex, colIndex);
                if (!match || !match.sets?.length) {
                    rowVals.push("-");
                    htmlRow += `<td style="text-align: center; color: ${T.muted}; border: 1px solid ${T.border};">-</td>`;
                    return;
                }

                const scoreStr = match.sets.map(set => {
                    const g1 = set.game_score_1 != null ? set.game_score_1 : 0;
                    const g2 = set.game_score_2 != null ? set.game_score_2 : 0;
                    return rowIndex < colIndex ? `${g1}:${g2}` : `${g2}:${g1}`;
                }).join(" ");

                const isWin = match.winner_side === (rowIndex < colIndex ? 1 : 2);
                rowVals.push(scoreStr || "-");
                htmlRow += `<td style="text-align: center; font-weight: ${isWin ? 'bold' : 'normal'}; color: ${isWin ? T.darkerGreen : T.muted}; border: 1px solid ${T.border};">${scoreStr || "-"}</td>`;
            });

            const diffVal = p?.games != null ? p.games : (dynamicStats[rowIndex].hasStats ? dynamicStats[rowIndex].games : 0);
            const ptsVal = p?.points != null ? p.points : (dynamicStats[rowIndex].hasStats ? dynamicStats[rowIndex].points : 0);
            const diff = diffVal !== 0 ? `${diffVal > 0 ? "+" : ""}${diffVal}` : "0";
            const pts = String(ptsVal);
            const place = p?.place || "—";

            rowVals.push(diff, pts, place);
            htmlRow += `<td style="text-align: center; border: 1px solid ${T.border};">${diff}</td><td style="text-align: center; font-weight: bold; color: ${T.ink}; border: 1px solid ${T.border};">${pts}</td><td style="text-align: center; font-weight: bold; background: ${T.superWhite}; color: ${T.ink}; border: 1px solid ${T.border};">${place}</td></tr>\n`;

            aoaGroup.push(rowVals);
            tsvAll.push(rowVals.join("\t"));
            htmlTable += htmlRow;
        });

        htmlTable += `</tbody></table></div>\n`;
        htmlAll += htmlTable;

        sheets[groupLabel] = aoaGroup;
    });

    htmlAll += `</div>`;

    return {
        sheets,
        tsv: tsvAll.join("\n"),
        html: htmlAll
    };
};

// ==================== PARTICIPANTS BUILDERS ====================\n

export const buildParticipantsDataset = (tournament, playersData, grade = 1) => {
    const list = Array.isArray(playersData) ? playersData : (playersData?.results || []);
    const { aoaHeader, htmlHeader } = getTournamentExportHeaderInfo(tournament, "");
    const tableHeaders = ["№", t("Participant"), t("City / Country"), t("Rating")];

    const aoa = [
        ...aoaHeader,
        tableHeaders
    ];

    if (!list.length) {
        return {
            sheets: { [t("Participants")]: [...aoa, [t("No data available")]] },
            tsv: `${t("Participants")}\n${t("No data available")}`,
            html: `<div>${htmlHeader}<p style="color: ${T.muted}; font-style: italic;">${t("No data available")}</p></div>`
        };
    }

    const tsvRows = [tableHeaders.join("\t")];
    let html = `<div>\n${htmlHeader}\n<table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; border: 1px solid ${T.border};">\n`;
    html += `<thead><tr>`;
    tableHeaders.forEach(th => {
        html += `<th style="background: ${T.superWhite}; color: ${T.ink}; font-weight: 700; border: 1px solid ${T.border}; padding: 8px;">${th}</th>`;
    });
    html += `</tr></thead><tbody>\n`;

    list.forEach((item, index) => {
        let name = "—";
        let cityCountry = "";
        let rating = "—";

        if (grade === 1) {
            name = formatEntryListName(item);
            const countryStr = item?.country ? t(getOptionName(COUNTRY_OPTIONS, item.country)) : null;
            cityCountry = [item?.city, countryStr && !/^\d+$/.test(String(countryStr).trim()) ? countryStr : null].filter(Boolean).join(", ");
            rating = item?.rating != null ? item.rating : "—";
        } else if (grade === 2) {
            const p1 = item?.players?.[0];
            const p2 = item?.players?.[1];
            name = formatEntryListDoublesPair(p1, p2);
            const rawCountry = p1?.country || p2?.country;
            const countryStr = rawCountry ? t(getOptionName(COUNTRY_OPTIONS, rawCountry)) : null;
            cityCountry = [p1?.city || p2?.city, countryStr && !/^\d+$/.test(String(countryStr).trim()) ? countryStr : null].filter(Boolean).join(", ");
            const pairSum = (p1?.rating || 0) + (p2?.rating || 0);
            rating = item?.team_rating != null ? item.team_rating : (pairSum > 0 ? pairSum : "—");
        } else {
            const teamName = item?.name || `${t("Team")} ${index + 1}`;
            const members = formatTeamMembers(item?.players);
            name = members ? `${teamName} — ${members}` : teamName;
            rating = item?.team_rating || "—";
        }

        const row = [index + 1, name, cityCountry || "—", rating];
        aoa.push(row);
        tsvRows.push(row.join("\t"));
        html += `<tr style="background: ${index % 2 === 1 ? T.superWhite : T.white};"><td style="text-align: center; font-weight: bold; background: ${T.superWhite}; color: ${T.ink}; border: 1px solid ${T.border}; padding: 7px;">${index + 1}</td><td style="font-weight: 500; border: 1px solid ${T.border}; padding: 7px;">${name}</td><td style="color: ${T.muted}; border: 1px solid ${T.border}; padding: 7px;">${cityCountry || "—"}</td><td style="text-align: center; font-weight: bold; color: ${T.ink}; border: 1px solid ${T.border}; padding: 7px;">${rating}</td></tr>\n`;
    });

    html += `</tbody></table></div>`;

    return {
        sheets: { [t("Participants")]: aoa },
        tsv: tsvRows.join("\n"),
        html
    };
};

// ==================== RESULTS BUILDERS ====================\n

export const buildResultsDataset = (tournament, resultsData, grade = 1) => {
    const list = Array.isArray(resultsData) ? resultsData : (resultsData?.results || []);
    const { aoaHeader, htmlHeader } = getTournamentExportHeaderInfo(tournament, "");
    const tableHeaders = [t("Place"), t("Participant"), t("Matches"), t("Wins"), t("Losses"), t("+/-"), t("Rating")];

    const aoa = [
        ...aoaHeader,
        tableHeaders
    ];

    if (!list.length) {
        return {
            sheets: { [t("Results")]: [...aoa, [t("No data available")]] },
            tsv: `${t("Results")}\n${t("No data available")}`,
            html: `<div>${htmlHeader}<p style="color: ${T.muted}; font-style: italic;">${t("No data available")}</p></div>`
        };
    }

    const tsvRows = [tableHeaders.join("\t")];
    let html = `<div>\n${htmlHeader}\n<table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; border: 1px solid ${T.border};">\n`;
    html += `<thead><tr>`;
    tableHeaders.forEach(th => {
        html += `<th style="background: ${T.superWhite}; color: ${T.ink}; font-weight: 700; border: 1px solid ${T.border}; padding: 8px;">${th}</th>`;
    });
    html += `</tr></thead><tbody>\n`;

    list.forEach((item, index) => {
        let name = "—";
        if (grade === 1) {
            name = formatPlayerName(item?.player || item);
        } else if (grade === 2) {
            const players = item?.team?.players || item?.players || [];
            name = formatDoublesPair(players[0], players[1]);
        } else {
            name = item?.team?.name || item?.name || `Team #${index + 1}`;
        }

        const place = (item?.tournament_place && item.tournament_place !== 0 && item.tournament_place !== "0") ? item.tournament_place : "—";
        const matches = item?.tournament_matches ?? 0;
        const wins = item?.tournament_wins ?? 0;
        const losses = item?.tournament_losses ?? 0;
        const diff = item?.games_diff != null ? `${item.games_diff > 0 ? "+" : ""}${item.games_diff}` : "—";
        const points = item?.tournament_score ?? 0;

        const row = [place, name, matches, wins, losses, diff, points];
        aoa.push(row);
        tsvRows.push(row.join("\t"));
        html += `<tr style="background: ${index % 2 === 1 ? T.superWhite : T.white};"><td style="text-align: center; font-weight: bold; background: ${T.superWhite}; color: ${T.ink}; border: 1px solid ${T.border}; padding: 7px;">${place}</td><td style="font-weight: 500; border: 1px solid ${T.border}; padding: 7px;">${name}</td><td style="text-align: center; border: 1px solid ${T.border}; padding: 7px;">${matches}</td><td style="text-align: center; color: ${T.ink}; font-weight: bold; border: 1px solid ${T.border}; padding: 7px;">${wins}</td><td style="text-align: center; color: ${T.alarm}; border: 1px solid ${T.border}; padding: 7px;">${losses}</td><td style="text-align: center; border: 1px solid ${T.border}; padding: 7px;">${diff}</td><td style="text-align: center; font-weight: bold; color: ${T.ink}; border: 1px solid ${T.border}; padding: 7px;">${points}</td></tr>\n`;
    });

    html += `</tbody></table></div>`;

    return {
        sheets: { [t("Results")]: aoa },
        tsv: tsvRows.join("\n"),
        html
    };
};

// ==================== BRACKETS BUILDERS ====================\n

export const buildBracketsDataset = (tournament, stageName, bracketsData, grade = 1, i18n) => {
    const draws = Array.isArray(bracketsData) ? bracketsData : (bracketsData?.draws || []);
    const { aoaHeader, htmlHeader } = getTournamentExportHeaderInfo(tournament, stageName);

    if (!draws.length) {
        return {
            sheets: { [stageName || "Sheet1"]: [...aoaHeader, [t("No data available")]] },
            tsv: `${stageName || t("Brackets")}\n${t("No data available")}`,
            html: `<div>${htmlHeader}<p style="color: ${T.muted}; font-style: italic;">${t("No data available")}</p></div>`
        };
    }

    const sheets = {};
    const tsvAll = [];
    let htmlAll = `<div>\n${htmlHeader}\n`;

    draws.forEach((draw, dIdx) => {
        const drawLabel = draw.name || `${draw.number}${getOrdinalSuffix(draw.number)} ${t("Place")}`;
        const tableHeaders = [t("Round"), "№", t("Participant 1"), t("Participant 2"), t("Score"), t("Winner")];

        const aoaDraw = [
            ...aoaHeader,
            [drawLabel],
            tableHeaders
        ];

        tsvAll.push(`\n=== ${drawLabel} ===`);
        tsvAll.push(tableHeaders.join("\t"));

        let htmlTable = `<div style="margin-bottom: 24px; border: 1px solid ${T.border}; border-radius: 6px; overflow: hidden;">\n`;
        htmlTable += `<div style="background: ${T.superWhite}; color: ${T.ink}; border-bottom: 1px solid ${T.border}; padding: 8px 12px; font-weight: bold; font-size: 14px;">${drawLabel}</div>\n`;
        htmlTable += `<table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; width: 100%; font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; border: 1px solid ${T.border};">\n`;
        htmlTable += `<thead><tr>`;
        tableHeaders.forEach(th => {
            htmlTable += `<th style="background: ${T.superWhite}; color: ${T.ink}; font-weight: 700; border: 1px solid ${T.border}; padding: 7px;">${th}</th>`;
        });
        htmlTable += `</tr></thead><tbody>\n`;

        (draw.matches || []).forEach((m, mIdx) => {
            const { side1: rawSide1, side2: rawSide2 } = getMatchParticipantNames(m, grade);
            // seed1/seed2 are only ever set on round-1 slots (BracketClassic.js/
            // Bracket.js) - prefixing the name is the same convention those
            // components' own seed column already uses, just inline here since
            // this export has no dedicated seed column of its own.
            const side1 = m.round_number === 1 && m.seed1 ? `(${m.seed1}) ${rawSide1}` : rawSide1;
            const side2 = m.round_number === 1 && m.seed2 ? `(${m.seed2}) ${rawSide2}` : rawSide2;
            const scoreText = getMatchScoreText(m);
            const roundTitle = getRoundTitle(m.round_number, draw.total || 4, t, i18n, m.match_order, false);
            const winnerName = m.winner_side === 1 ? side1 : m.winner_side === 2 ? side2 : "—";

            const rowVals = [
                roundTitle,
                m.match_number || mIdx + 1,
                side1,
                side2,
                scoreText || "—",
                winnerName
            ];

            aoaDraw.push(rowVals);
            tsvAll.push(rowVals.join("\t"));
            htmlTable += `<tr style="background: ${mIdx % 2 === 1 ? T.superWhite : T.white};">
                <td style="color: ${T.muted}; border: 1px solid ${T.border}; padding: 7px;">${roundTitle}</td>
                <td style="text-align: center; color: ${T.muted}; border: 1px solid ${T.border}; padding: 7px;">${m.match_number || mIdx + 1}</td>
                <td style="font-weight: ${m.winner_side === 1 ? 'bold' : '500'}; color: ${m.winner_side === 1 ? T.darkerGreen : T.ink}; border: 1px solid ${T.border}; padding: 7px;">${side1}</td>
                <td style="font-weight: ${m.winner_side === 2 ? 'bold' : '500'}; color: ${m.winner_side === 2 ? T.darkerGreen : T.ink}; border: 1px solid ${T.border}; padding: 7px;">${side2}</td>
                <td style="text-align: center; font-weight: bold; color: ${T.ink}; border: 1px solid ${T.border}; padding: 7px;">${scoreText || "—"}</td>
                <td style="font-weight: bold; color: ${T.darkerGreen}; border: 1px solid ${T.border}; padding: 7px;">${winnerName}</td>
            </tr>\n`;
        });

        htmlTable += `</tbody></table></div>\n`;
        htmlAll += htmlTable;

        sheets[drawLabel] = aoaDraw;
    });

    htmlAll += `</div>`;

    return {
        sheets,
        tsv: tsvAll.join("\n"),
        html: htmlAll
    };
};
